import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";
import { gtagTrackYearlyPricingClick } from "../util/GoogleTagUtils";
import { assignToVariant, trackPricingClick, PRICING_TEST_ID, PRICING_TEST_VARIANTS } from "../util/ABTestUtils";
import checkIcon from "../images/zta_checkmark.svg";

const useStyles = makeStyles({
  pricingContainer: {
    display: "flex",
    gap: 16,
    margin: "auto",
    justifyContent: "left",
    maxWidth: "1200px",
    marginBottom: "10%",
    "@media (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "center",
    },
  },
  pricingCard: {
    flex: 1,
    flexDirection: "column",
    fontFamily: "Montserrat",
    position: "relative",
    fontWeight: "800",
    margin: "14px",
    opacity: 0.8,
    boxShadow: "10px 5px 20px black",
    backgroundColor: "#141721",
    color: "black",
    width: "100%",
    maxWidth: "90vw",
    padding: "20px",
    borderRadius: "10px",
    minHeight: "650px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid #44d8a4",
    "@media (max-width: 768px)": {
      minHeight: "auto",
      paddingBottom: "40px",
      width: "95%",
      maxWidth: "400px",
    },
  },
  priceText: {
    fontWeight: "900",
    color: "#44d8a4",
    fontFamily: "Montserrat",
    marginTop: "14px",
    marginBottom: "14px",
    fontSize: "2rem",
    "@media (max-width: 768px)": {
      fontSize: "1.8rem",
    },
  },
  features: {
    fontSize: "1rem",
    color: "lightgray",
    textAlign: "left",
    fontWeight: 400,
    lineHeight: 2.0,
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingBottom: "16px",
    width: "100%",
    "@media (max-width: 768px)": {
      fontSize: "0.85rem",
      lineHeight: 1.6,
      padding: "0 10px",
    },
  },
  pricingCTAContainer: {
    justifyContent: "center",
    display: "flex",
    marginTop: "auto",
    paddingBottom: "20px",
    "@media (max-width: 768px)": {
      paddingTop: "10px",
    },
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    width: "100%",
    "@media (max-width: 768px)": {
      fontSize: "0.85rem",
      lineHeight: 1.6,
      marginBottom: "8px",
    },
  },
  featureText: {
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "@media (max-width: 768px)": {
      fontSize: "0.85rem",
    },
  },
  checkIcon: {
    width: "16px",
    height: "16px",
    marginTop: "5px",
    flexShrink: 0,
  },
});

const PricingCards = () => {
  const classes = useStyles();
  const [pricingVariant, setPricingVariant] = useState(null);

  useEffect(() => {
    // Assign user to a pricing variant when component mounts
    const variant = assignToVariant(PRICING_TEST_ID, Object.values(PRICING_TEST_VARIANTS));
    console.log(`[PricingCards] Component mounted, variant assigned: ${variant}`);
    setPricingVariant(variant);
  }, []);

  const handlePricingClick = () => {
    const price = pricingVariant === PRICING_TEST_VARIANTS.ANNUAL ? 1997 : 197;
    console.log(`[PricingCards] Pricing click - variant: ${pricingVariant}, price: ${price}`);
    trackPricingClick(pricingVariant, price);
    gtagTrackYearlyPricingClick();
  };

  const getPriceText = () => {
    if (pricingVariant === PRICING_TEST_VARIANTS.ANNUAL) {
      return "$1,997/year";
    } else if (pricingVariant === PRICING_TEST_VARIANTS.MONTHLY) {
      return "$197/month";
    }
    return "$1,997/year"; // Default fallback
  };

  return (
    <div id="pricing-section">
      <div className={classes.pricingContainer}>
        <Card className={classes.pricingCard}>
          <CardContent style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Typography variant="h5" component="h2" className="text-3xl font-extrabold text-[#CCFFFF]">
              ZTA Quant Portfolio
            </Typography>
            <Typography variant="h3" component="h3" className={classes.priceText}>
              {getPriceText()}
            </Typography>
            <ul className={classes.features}>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>No setup fee</span>
              </li>
              {/*
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>30-day <strong>money back guarantee</strong></span>
              </li>
              */}
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>12 algos trading ES, NQ, GC, CL, NG</span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>
                  <Link to="/portfolio_analyzer" className="underline text-[#CCFFFF]">
                    Portfolio Analyzer
                  </Link>
                  {" "}license
                </span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>
                  <Link to="/book-demo#risk-management" className="underline text-[#CCFFFF]">MoneyManager</Link> plugin license
                </span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>Discord community access</span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>Strategy templates & settings</span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>24/7 Email support</span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>Chat Support</span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>System upgrades & improvements</span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>Optimization guide</span>
              </li>
              <li className={classes.featureItem}>
                <img src={checkIcon} alt="✔" className={classes.checkIcon} />
                <span className={classes.featureText}>Unlimited access to future algos</span>
              </li>
            </ul>
            <div className={classes.pricingCTAContainer}>
              <Link
                to="/portfolios/zta_quant_portfolio"
                onClick={handlePricingClick}
                className="justify-center flex p-1"
              >
                <div className="buttons">
                  <button className="btn">
                    <span></span>
                    <p data-text="Let's Go!" data-title="Get Started!"></p>
                  </button>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PricingCards;
